import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Trophy, Clock, Target, CheckCircle } from '@phosphor-icons/react';
import IncentivoCelebration from '@/components/IncentivoCelebration';

const CELEBRATED_KEY = 'incentivos_celebrados';

const eur = (v) =>
  new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const imgSrc = (u) => (u?.startsWith('http') ? u : `${BACKEND}${u}`);

function useCountdown(dateStr) {
  const [left, setLeft] = useState('');
  useEffect(() => {
    if (!dateStr) return;
    const end = new Date(`${dateStr}T23:59:59`).getTime();
    const tick = () => {
      const diff = end - Date.now();
      if (diff <= 0) { setLeft('Terminada'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setLeft(`${d}d ${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dateStr]);
  return left;
}

function CampaignCard({ c }) {
  const countdown = useCountdown(c.data_fim);
  const pct = Math.min(100, c.percentagem || 0);
  return (
    <div
      data-testid={`incentivo-card-${c.id}`}
      className="rounded-2xl border border-[#E7E2D6] bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center text-amber-600">
            <Trophy size={22} weight="fill" />
          </div>
          <div>
            <h3 className="font-semibold text-[#0F1829] leading-tight">{c.nome}</h3>
            {c.premio && <p className="text-xs text-amber-700 font-medium">🎁 {c.premio}</p>}
          </div>
        </div>
        {c.atingiu && (
          <span data-testid={`incentivo-atingido-${c.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
            <CheckCircle size={14} weight="fill" /> Objetivo atingido
          </span>
        )}
      </div>

      {c.descricao && <p className="text-sm text-[#6B7280] mt-3">{c.descricao}</p>}

      {(c.imagens_urls || []).length > 0 && (
        <div className={`mt-3 grid gap-2 ${c.imagens_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`} data-testid={`incentivo-imagens-${c.id}`}>
          {c.imagens_urls.map((u, i) => (
            <img key={i} src={imgSrc(u)} alt={c.nome}
              className={`w-full rounded-xl object-cover border border-[#E7E2D6] ${c.imagens_urls.length === 1 ? 'h-44' : 'h-28'}`} />
          ))}
        </div>
      )}

      <div className="mt-4">
        <div className="flex items-end justify-between text-sm mb-1">
          <span className="text-[#6B7280]">Conseguido</span>
          <span className="font-bold text-[#0F1829]" data-testid={`incentivo-conseguido-${c.id}`}>{eur(c.conseguido)}</span>
        </div>
        <div className="h-3 rounded-full bg-[#F1EEE6] overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${c.atingiu ? 'bg-emerald-500' : 'bg-amber-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-[#6B7280] mt-1">
          <span className="inline-flex items-center gap-1"><Target size={13} /> Objetivo {eur(c.valor_objetivo)}</span>
          <span>{pct}%</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-xl bg-[#FAF8F2] p-3">
          <p className="text-[11px] uppercase tracking-wide text-[#978D78]">Falta alcançar</p>
          <p className="text-lg font-bold text-[#0F1829]" data-testid={`incentivo-falta-${c.id}`}>
            {c.atingiu ? '—' : eur(c.falta)}
          </p>
        </div>
        <div className="rounded-xl bg-[#0F1829] text-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-white/60 inline-flex items-center gap-1"><Clock size={12} /> Termina em</p>
          <p className="text-lg font-bold font-mono" data-testid={`incentivo-countdown-${c.id}`}>{countdown}</p>
        </div>
      </div>

      <div className="flex gap-4 mt-3 text-xs text-[#6B7280]">
        <span>Pessoais: <b className="text-[#0F1829]">{eur(c.conseguido_pessoal)}</b></span>
        <span>Vendas diretas: <b className="text-[#0F1829]">{eur(c.conseguido_direto)}</b></span>
      </div>
    </div>
  );
}

export default function IncentivosPage() {
  const [campaigns, setCampaigns] = useState(null);
  const [error, setError] = useState('');
  const [queue, setQueue] = useState([]); // campanhas atingidas por celebrar (uma de cada vez)

  useEffect(() => {
    api.get('/crm/incentivos/ativas')
      .then((r) => {
        const data = r.data || [];
        setCampaigns(data);
        // Consultor que atingiu/excedeu o objetivo → celebração (uma vez por campanha).
        let done = {};
        try { done = JSON.parse(localStorage.getItem(CELEBRATED_KEY) || '{}'); } catch (_) { done = {}; }
        const pending = data.filter((c) => c.is_consultor && c.atingiu && !done[c.id]);
        if (pending.length) {
          pending.forEach((c) => { done[c.id] = true; });
          try { localStorage.setItem(CELEBRATED_KEY, JSON.stringify(done)); } catch (_) { /* noop */ }
          setQueue(pending);
        }
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Erro ao carregar campanhas'));
  }, []);

  const celebrate = queue[0] || null;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="incentivos-page">
      {celebrate && (
        <IncentivoCelebration
          key={celebrate.id}
          campaign={celebrate}
          onClose={() => setQueue((q) => q.slice(1))}
        />
      )}
      <div className="flex items-center gap-2 mb-1">
        <Trophy size={24} weight="fill" className="text-amber-500" />
        <h1 className="text-2xl font-bold text-[#0F1829]">Campanhas de Incentivos</h1>
      </div>
      <p className="text-sm text-[#6B7280] mb-6">
        Acompanhe o seu progresso e o que falta para alcançar cada prémio. O volume inclui as suas
        compras pessoais e as vendas diretas a Clientes, Afiliados e Embaixadores.
      </p>

      {error && <div className="text-red-600 text-sm" data-testid="incentivos-error">{error}</div>}
      {campaigns === null && !error && <div className="text-[#6B7280]">A carregar…</div>}
      {campaigns?.length === 0 && (
        <div className="text-center py-16 text-[#978D78]" data-testid="incentivos-empty">
          <Trophy size={40} className="mx-auto mb-3 opacity-40" />
          Não há campanhas de incentivos ativas para si de momento.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {(campaigns || []).map((c) => <CampaignCard key={c.id} c={c} />)}
      </div>
    </div>
  );
}
